import React, { memo } from 'react';
import HeroFigure from '../../common/HeroFigure';
import { MAX_GUARD, MAX_MANA, spellMeta } from '../engine/constants';
import { useTranslation } from 'react-i18next';
import { tr } from '../../../../i18n/msg';

/**
 * Fighting-game style top bar: you (left) · timer · opponent (right).
 * Each side: hero portrait (the real assembled look), name / power / spell, HP bar with a
 * damage-lag trail (the pink part shrinks after a delay), mana and guard meters.
 */

function Portrait({ meta, side }) {
  return (
    <div className={`bh-portrait bh-portrait--${side}`}>
      <HeroFigure
        look={meta?.look}
        character={meta?.kind && meta.kind !== 'hero' ? meta.kind : meta?.character}
        size={54}
        facing={side === 'left' ? 1 : -1}
        animated={false}
        title={meta?.name}
      />
    </div>
  );
}

function SideBar({ meta, f, side }) {
  const { t } = useTranslation();
  const maxHp = Number(meta?.maxHp) || 1;
  const hp = Math.max(0, Number(f?.hp ?? maxHp));
  const pct = Math.max(0, Math.min(100, (hp / maxHp) * 100));
  const mana = Math.max(0, Math.min(100, ((Number(f?.mn) || 0) / MAX_MANA) * 100));
  const g = Number(f?.gd);
  const guard = Math.max(0, Math.min(100, ((Number.isFinite(g) ? g : MAX_GUARD) / MAX_GUARD) * 100));
  const sp = spellMeta(meta?.spell, meta?.character);
  return (
    <div className={`bh-side bh-side--${side}${pct <= 25 ? ' is-danger' : ''}`}>
      <Portrait meta={meta} side={side} />
      <div className="bh-side__bars">
        <div className="bh-name">
          <span className="bh-name__text">{meta?.name || (side === 'left' ? t('brawl.you', 'You') : t('brawl.opponent', 'Opponent'))}</span>
          {meta?.power ? <span className="bh-power">⚡{meta.power}</span> : null}
          <span className="bh-spell" title={tr(t, sp.name)}>{sp.icon}</span>
        </div>
        <div className="bh-hp">
          <div className="bh-hp__lag" style={{ width: `${pct}%` }} />
          <div className="bh-hp__fill" style={{ width: `${pct}%` }} />
          <span className="bh-hp__text">{Math.ceil(hp)}</span>
        </div>
        <div className="bh-meters">
          <div className="bh-mana"><div className={`bh-mana__fill${mana >= 100 ? ' is-full' : ''}`} style={{ width: `${mana}%` }} /></div>
          <div className="bh-guard"><div className={`bh-guard__fill${guard < 30 ? ' is-low' : ''}`} style={{ width: `${guard}%` }} /></div>
        </div>
      </div>
    </div>
  );
}

function BrawlHud({
  me, opp, frame, timeLimitSec, center, className = '',
}) {
  const fMe = frame?.f.find((x) => x.i === me?.idx);
  const fOpp = opp ? frame?.f.find((x) => x.i === opp.idx) : null;
  const tm = frame ? Math.max(0, Math.ceil((frame.tm || 0) / 1000)) : timeLimitSec;
  return (
    <div className={`bh ${className}`}>
      <SideBar meta={me} f={fMe} side="left" />
      <div className="bh-center">
        {center ?? <div className={`bh-timer${tm <= 10 ? ' is-low' : ''}`}>{Number.isFinite(tm) ? tm : '∞'}</div>}
      </div>
      {opp ? <SideBar meta={opp} f={fOpp} side="right" /> : <div className="bh-side bh-side--right" />}
    </div>
  );
}

export default memo(BrawlHud);
